import { differentiate, stats, type Stats } from './channels'
import { MOMENT_JOINTS, planeMoments, type DynamicsResult, type MomentJoint } from './dynamics'
import { SIDES, type GaitResult, type Side } from './gait'
import type { PlaneKey } from './joints'
import { planeLimits, type HumanoidModel, type PlaneLimit } from './model'
import { CATALOG_MOTORS, setAxisFor, type CatalogMotor } from './motorisation'
import { planeOff } from './off'
import type { OffSet, OffState } from './state'

/**
 * Antriebsauslegung — was jede Achse WIRKLICH braucht.
 *
 * Die Kette endet hier: aus Gelenkpunkten wurde eine Bewegung, aus der Bewegung
 * wurden Momente, und aus den Momenten wird jetzt eine Anforderung an den
 * Antrieb. Zwei Dinge, die dabei streng getrennt bleiben:
 *
 *   ANFORDERUNG — was die Bewegung am Abgang verlangt. Das ist Physik: Drehzahl,
 *   Beschleunigung, Moment, Leistung, Energie je Zyklus. Sie hängt an keinem
 *   Katalog und ändert sich nicht, wenn jemand einen anderen Motor vorschlägt.
 *
 *   VORSCHLAG — welcher Motor mit welcher Übersetzung das leisten könnte. Das ist
 *   eine Empfehlung und wird auch so benannt. Ein Motorexperte soll sie
 *   überstimmen können, ohne dass irgendetwas an der Anforderung wackelt.
 *
 * DREI SÄTZE, DIE DIE AUSLEGUNG BESTIMMEN — und die in keinem Motorprospekt
 * nebeneinanderstehen:
 *
 * 1. **Die Spitze entscheidet, ob es geht; der Effektivwert, ob es hält.** Die
 *    Verlustleistung geht mit dem QUADRAT des Moments. Ein Antrieb, der nach der
 *    Spitze gewählt wird, ist im Dauerbetrieb entweder überdimensioniert oder er
 *    brennt.
 *
 * 2. **Die Rotorträgheit wird mit dem QUADRAT der Übersetzung an die Achse
 *    gespiegelt.** Bei i = 80 wird aus 10⁻⁴ kg·m² am Rotor 0,64 kg·m² an der
 *    Achse. Deshalb gibt es eine beste Übersetzung, und sie ist nicht die
 *    grösstmögliche.
 *
 * 3. **Der thermisch kritische Punkt ist das HALTEN im Stillstand.** Dort ist das
 *    Moment gross, die Drehzahl null und das Produkt — die Leistung — ebenfalls
 *    null. Für diesen Betriebspunkt ist die Wattzahl eines Motors bedeutungslos.
 *    Was zählt, ist die Motorkonstante Km = Kt/√R: die Verlustleistung ist
 *    (M/Km)².
 *
 * WAS HIER NICHT GERECHNET WIRD, und das gehört gesagt:
 *
 *   · **Das Getriebe ist eine Übersetzung und ein Wirkungsgrad, sonst nichts.**
 *     Seine Masse, sein zulässiges Abtriebsmoment, sein Verdrehspiel und seine
 *     Steifigkeit fehlen. Ein Vorschlag mit i = 160 kann deshalb rechnerisch
 *     passen und mechanisch unbaubar sein.
 *   · **Die Bauform fehlt.** Ob der Motor an die Achse passt oder 200 mm
 *     proximal sitzen muss, ändert die Trägheit der ganzen Kette — und damit die
 *     Anforderung an die Achse darüber.
 *   · **Nur der gerechnete Zyklus zählt.** Treppe, Aufstehen, Stossbelastung und
 *     Sturz sind eigene Lastfälle und stehen hier nicht.
 */

/* -------------------------------------------------------------------------
   Die Achse
   ------------------------------------------------------------------------- */

/**
 * Eine Antriebsachse: ein Gelenk in einer Ebene, auf einer Seite.
 *
 * Nicht zu verwechseln mit  im Modell — das ist die KONSTRUKTIONSachse
 * (x, y, z) einer Winkelfreiheit. Hier geht es um die Ebene, in der ein Antrieb
 * arbeitet.
 */
export interface DriveAxis {
  joint: MomentJoint
  plane: PlaneKey
  side: Side
}

export const axisName = (a: DriveAxis): string => `${a.joint}|${a.plane}|${a.side}`

/** Bewegung und Moment am ABGANG — was die Aufgabe verlangt. */
export interface AxisDemand extends DriveAxis {
  label: string
  /** Winkel in Grad, über den Zyklus. */
  angle: Stats
  /** Winkelgeschwindigkeit am Abgang, rad/s. */
  speed: Stats
  /** Winkelbeschleunigung am Abgang, rad/s². */
  acceleration: Stats
  /** Moment am Abgang, Nm. */
  moment: Stats
  /**
   * Der Momentenverlauf selbst, Nm je Abtastpunkt.
   *
   * Für den Antrieb genügt die Spitze; für den KUPFERVERLUST nicht. Der wächst
   * mit dem Quadrat des Moments und fällt deshalb dort an, wo gehalten wird —
   * nicht dort, wo die Spitze steht.
   */
  momentTrack: Float32Array
  /** Mechanische Leistung am Abgang, W. Positiv = treibend. */
  power: Stats
  /**
   * Der Leistungsverlauf selbst, W je Abtastpunkt.
   *
   * Die Kennwerte allein reichen für die Auslegung des Antriebs, nicht aber für
   * die des ZWISCHENKREISES: dort zählt die Summe über alle Achsen zu jedem
   * Zeitpunkt. Eine Achse, die bremst, während eine andere treibt, entlastet
   * den Akku — das steht in keiner Spitze und in keinem Effektivwert.
   */
  powerTrack: Float32Array
  /** Arbeit je Zyklus, treibend, J. */
  workDriving: number
  /** Arbeit je Zyklus, bremsend (Betrag), J. */
  workBraking: number
  /**
   * Grösstes Moment bei nahezu stillstehender Achse, Nm.
   *
   * DER THERMISCH KRITISCHE PUNKT. Hier gibt der Antrieb keine Leistung ab und
   * setzt trotzdem die volle Verlustleistung in Wärme um — ohne dass Bewegung
   * sie abführt.
   */
  holdingMoment: number
  /** Die Grenzen der Achse aus den Winkelfreiheiten, falls es welche gibt. */
  limit: PlaneLimit | null
  /**
   * Es gibt für diese Ebene KEINE Winkelfreiheit — das Moment trägt die
   * Struktur, nicht ein Antrieb.
   *
   * Das ist eine der nützlichsten Auskünfte der ganzen Liste. Am Knie fallen im
   * Gehen frontal 58 Nm an, und das Knie hat nur eine Nickachse: diese 58 Nm
   * gehen in Lager und Struktur. Wer sie als Antriebsanforderung liest, legt
   * einen Motor für eine Achse aus, die es nicht gibt; wer sie gar nicht sieht,
   * unterdimensioniert das Lager.
   */
  structural: boolean
  /**
   * Diese Achse ist STILLGELEGT — sie steht auf ihrem Rastwinkel.
   *
   * Zwei Fälle, und der Unterschied ist die halbe Auslegung: bleibt der Antrieb
   * verbaut, hält er nur noch und wird nach dem Haltemoment ausgelegt; ist er
   * ausgebaut, trägt die Struktur, und die Zeile ist `structural`.
   */
  stopped: OffState | null
  /** Ausnutzung des zulässigen Moments, 1 = an der Grenze. */
  momentUse: number | null
  /** Ausnutzung des Bewegungsbereichs, 1 = an der Grenze. */
  rangeUse: number | null
}

/** Unterhalb dieser Drehzahl gilt die Achse als haltend. */
const HOLDING_SPEED = 0.35

const DEG = Math.PI / 180

/** Gelenknamen — auch die Stückliste beschriftet damit ihre Achsen. */
export const JOINT_LABELS: Record<string, string> = {
  hip: 'Hüfte',
  knee: 'Knie',
  ankle: 'Sprunggelenk',
  shoulder: 'Schulter',
  elbow: 'Ellbogen',
  /* Die Antriebsliste kennt nur die fünf oben — sie führt nur Achsen, für die
     es Bewegung UND Moment gibt. Die Grenzlagenprüfung geht über ALLE Gelenke
     mit Winkelfreiheiten, und dort tauchen diese hier auf. Ohne Eintrag stünde
     dort der Schlüssel aus dem Modell: „mtp · sagittal". */
  mtp: 'Zehengrundgelenk',
  pelvis: 'Becken',
  lumbar: 'Lendenwirbelsäule',
  thorax: 'Brustwirbelsäule',
}

export const PLANE_LABELS: Record<PlaneKey, string> = {
  sag: 'sagittal',
  fro: 'frontal',
  tra: 'transversal',
}

/**
 * Die Anforderung aller Achsen aus einem gerechneten Zyklus.
 *
 * Eine Achse taucht auf, wenn es für sie eine Bewegung UND ein Moment gibt.
 * Achsen ohne Bewegungsvorgabe (im gerechneten Gang etwa das Zehengrundgelenk)
 * werden nicht erfunden — sie fehlen, und das ist die richtige Auskunft.
 */
export function axisDemands(
  model: HumanoidModel,
  gait: GaitResult,
  dynamics: DynamicsResult,
  off: OffSet = {},
): AxisDemand[] {
  const n = dynamics.samples
  const limits = planeLimits(model, Object.keys(off))
  const out: AxisDemand[] = []

  for (const joint of MOMENT_JOINTS) {
    for (const side of SIDES) {
      const moments = planeMoments(dynamics, joint, side)
      for (const plane of ['sag', 'fro', 'tra'] as PlaneKey[]) {
        const angleTable = gait.angles[joint]?.[plane]?.[side]
        if (!angleTable) continue

        const angle = stats(angleTable, n)
        /* Eine Achse, die sich nicht bewegt und kein Moment trägt, ist keine
           Achse — sie im Bericht zu führen hiesse, einen Antrieb für nichts zu
           verlangen. */
        const momentTrack = moments[plane]
        const momentStats = stats(momentTrack, n)
        if (angle.max - angle.min < 1e-6 && momentStats.peak < 1e-6) continue

        /* Ableiten in GRAD, dann umrechnen — dieselbe Kette wie in den Kanälen,
           damit Diagramm und Auslegung dieselben Zahlen zeigen. */
        const kinematics = differentiate(angleTable, n, dynamics.cycleTime)
        const speedRad = new Float32Array(n)
        const accelRad = new Float32Array(n)
        for (let i = 0; i < n; i++) {
          speedRad[i] = kinematics.velocity[i] * DEG
          accelRad[i] = kinematics.acceleration[i] * DEG
        }

        /*
          Leistung an DIESER Achse: Moment mal Winkelgeschwindigkeit der Achse.
          Nicht die Gelenkleistung aus der Dynamik — die fasst alle drei Ebenen
          zusammen, und ein Antrieb sitzt auf einer.
        */
        const power = new Float32Array(n)
        for (let i = 0; i < n; i++) power[i] = momentTrack[i] * speedRad[i]

        const dt = dynamics.cycleTime / n
        let driving = 0
        let braking = 0
        let holding = 0
        for (let i = 0; i < n; i++) {
          if (power[i] > 0) driving += power[i] * dt
          else braking -= power[i] * dt
          if (Math.abs(speedRad[i]) < HOLDING_SPEED) {
            holding = Math.max(holding, Math.abs(momentTrack[i]))
          }
        }

        const limit = limits[joint]?.[plane] ?? null
        const stopped = planeOff(off, joint, plane) ?? null
        out.push({
          joint,
          plane,
          side,
          label: `${JOINT_LABELS[joint] ?? joint} ${side} · ${PLANE_LABELS[plane]}`,
          angle,
          speed: stats(speedRad, n),
          acceleration: stats(accelRad, n),
          moment: momentStats,
          momentTrack,
          power: stats(power, n),
          powerTrack: power,
          workDriving: driving,
          workBraking: braking,
          holdingMoment: holding,
          limit,
          /* Ohne Grenze gibt es keinen Antrieb — es sei denn, die Achse ist
             stillgelegt und ihr Antrieb bleibt verbaut. Dann hält er. */
          structural: limit === null && stopped?.drive !== true,
          stopped,
          momentUse: limit && limit.moment > 0 ? momentStats.peak / limit.moment : null,
          rangeUse: limit ? rangeUse(angle, limit) : null,
        })
      }
    }
  }
  return out
}

/** Wie weit die Kurve den Bewegungsbereich ausschöpft. Über 1 heisst: darüber hinaus. */
function rangeUse(angle: Stats, limit: PlaneLimit): number {
  const span = limit.hi - limit.lo
  if (span <= 0) return 0
  const over = Math.max(0, angle.max - limit.hi, limit.lo - angle.min)
  return (angle.max - angle.min + 2 * over) / span
}

/* -------------------------------------------------------------------------
   Der Katalog
   ------------------------------------------------------------------------- */

/**
 * Ein Motor — als DATEN, damit ein Motorexperte die Liste pflegen kann, ohne
 * eine Formel anzufassen.
 *
 * `torqueConstant` und `resistance` stehen einzeln und nicht nur als fertige
 * Motorkonstante, weil sie einzeln im Datenblatt stehen. Km wird daraus
 * gerechnet — eine dritte Zahl, die man nachpflegen müsste, gibt es nicht.
 */
export interface Motor {
  id: string
  label: string
  /** Momentkonstante Kt, Nm/A. */
  torqueConstant: number
  /**
   * Phasenwiderstand R, Ohm — oder `null`, wenn er nicht bekannt ist.
   *
   * `null` IST EINE ANTWORT UND KEIN FEHLENDER WERT. Der Gen5-Stand fuehrt zu
   * den FMK-Motoren Momente, Drehzahlen, Traegheit und Masse, aber keinen
   * Phasenwiderstand. Ohne ihn laesst sich die Motorkonstante Km = Kt/√R nicht
   * bilden, und damit auch der Halteverlust nicht.
   *
   * Vorher stand hier in solchen Faellen eine 1 — mit dem Ergebnis, dass Km
   * gleich Kt war und der Halteverlust eine Zahl, die richtig aussah und nichts
   * bedeutete. Eine Auslegung, die auf einer erfundenen Konstante steht, ist
   * schlechter als eine, die sagt: das kann ich nicht beurteilen.
   */
  resistance: number | null
  /**
   * Rotorträgheit, kg·m² — oder `null`, wenn sie nicht bekannt ist.
   *
   * Ohne sie gibt es keine Traegheitsanpassung: die Uebersetzung folgt dann
   * allein aus der Drehzahlgrenze, und das steht am Ergebnis. Der Gen5-Stand
   * nennt sie fuer jeden Motor, bei FMK07019 und FMK06018 als Schaetzung.
   */
  rotorInertia: number | null
  /**
   * Höchstdrehzahl, 1/min — oder 0, wenn sie nicht bekannt ist.
   *
   * Eine 0 hier heisst nicht „steht still", sondern „nicht veroeffentlicht" —
   * und die Drehzahlpruefung meldet dann nicht bewertbar statt „ueber 0 1/min".
   */
  speedMax: number
  /** Dauermoment am Rotor, Nm. */
  momentContinuous: number
  /** Spitzenmoment am Rotor, Nm. */
  momentPeak: number
  /** Masse, kg. */
  mass: number
  /**
   * Woher die Zahlen kommen.
   *
   * Steht am Datensatz und nicht in einer Fussnote: ein geschätzter Wert und ein
   * Datenblattwert sehen im Bericht sonst gleich aus, und genau daran scheitert
   * später die Entscheidung.
   */
  source: 'datenblatt' | 'geschaetzt' | 'entwurf'
  note?: string
}

/**
 * Motorkonstante Km = Kt/√R, in Nm/√W — oder `null` ohne Widerstand.
 *
 * Sie ist die Groesse, mit der man einen Hersteller anspricht: wie viel Moment
 * je Wurzel Verlustleistung. Ohne R gibt es sie nicht, und dann gibt es auch
 * keinen Halteverlust.
 */
export const motorConstant = (motor: Motor): number | null =>
  motor.resistance === null ? null : motor.torqueConstant / Math.sqrt(motor.resistance)

/**
 * DER KATALOG — die Motoren des Gen5-Stands (Kinco/Honpine FMK).
 *
 * HIER STANDEN ACHT MOTOREN EINES MODELLBAUHERSTELLERS, aus dem Diagramm
 * „Motorkonstante gegen Rotortraegheit" der Vorlage zurueckgerechnet, danach
 * die TQ-Kandidaten der Sammelmappe. Seit die Motorisierung gesetzt ist, waere
 * jeder andere Katalog eine zweite Wahrheit — die Seite schluege Motoren vor,
 * die es in diesem Projekt nicht gibt.
 *
 * WAS AUS DEM STAND KOMMT: Dauermoment, Spitzenmoment, Nenn- und
 * Hoechstdrehzahl, Rotortraegheit, Masse. Bei FMK07019 und FMK06018 sind
 * einzelne Werte geschaetzt oder angenommen — der Datensatz traegt das als
 * `source`.
 *
 * WAS FEHLT UND NICHT ERFUNDEN WIRD: Phasenwiderstand und Dauerstrom. Ohne
 * Widerstand gibt es keine Motorkonstante, ohne Dauerstrom keine
 * Momentkonstante aus dem Datenblatt (siehe `OPEN_QUESTIONS` in
 * `motorisation.ts`). Beide stehen deshalb auf `null` beziehungsweise 0, und
 * die Auslegung sagt „nicht bewertbar" statt eine Zahl zu zeigen, die richtig
 * aussieht und nichts bedeutet.
 */
export const MOTORS: Motor[] = CATALOG_MOTORS.map(catalogMotor)

/**
 * Vom Katalogeintrag des Stands zum Auslegungsdatensatz.
 *
 * Eine Uebersetzung und keine Rechnung. Sie steht als Funktion da, damit ein
 * neuer Motor im Stand hier nichts zu tun gibt.
 */
function catalogMotor(m: CatalogMotor): Motor {
  return {
    id: m.id,
    label: m.label,
    /* Kein Dauerstrom im Stand — ohne ihn keine Momentkonstante. Die 0 ist
       „unbekannt", nicht „null Nm/A"; mit R = null bleibt Km ohnehin null. */
    torqueConstant: 0,
    resistance: null,
    rotorInertia: m.rotorInertia,
    speedMax: m.speedMax,
    momentContinuous: m.momentNominal,
    momentPeak: m.momentPeak,
    mass: m.mass,
    source: m.source,
    note: m.note,
  }
}

/* -------------------------------------------------------------------------
   Getriebe und Vorschlag
   ------------------------------------------------------------------------- */

/** Randbedingungen der Auslegung — Daten, keine Konstanten im Code. */
export interface DriveSpec {
  /** Wirkungsgrad des Getriebes im Antriebsfall. */
  efficiency: number
  /** Kleinste sinnvolle Übersetzung. */
  ratioMin: number
  /** Grösste baubare Übersetzung. */
  ratioMax: number
  /** Sicherheitsfaktor auf das Dauermoment. */
  reserve: number
  /** Zulässige Wicklungsverlustleistung im Halten, W. */
  holdingLoss: number
}

export const DRIVE_SPEC: DriveSpec = {
  efficiency: 0.85,
  ratioMin: 4,
  ratioMax: 160,
  reserve: 1.2,
  holdingLoss: 60,
}

/**
 * Die Übersetzung, die das Motormoment kleinstmöglich macht.
 *
 * Am Motor liegt an: `M/(i·η)` für die Last und `J·i·α` für den eigenen Rotor.
 * Das erste fällt mit i, das zweite steigt mit i — dazwischen liegt ein
 * Minimum, und es steht nicht in einem Katalog, sondern folgt aus der Ableitung:
 *
 *     i* = √( M / (η · J · α) )
 *
 * Das ist die klassische Trägheitsanpassung. Wer stattdessen „möglichst viel
 * Übersetzung" wählt, zahlt den Rotor mit dem Quadrat.
 */
export function matchedRatio(
  demand: AxisDemand,
  motor: Motor,
  spec: DriveSpec = DRIVE_SPEC,
): number {
  const alpha = demand.acceleration.peak
  /* Ohne Rotortraegheit gibt es keine Anpassung — dann entscheidet allein die
     Drehzahlgrenze, und `proposeDrive` nimmt das Minimum aus beiden. */
  if (alpha < 1e-6 || motor.rotorInertia === null || motor.rotorInertia < 1e-12) {
    return spec.ratioMax
  }
  const ideal = Math.sqrt(demand.moment.peak / (spec.efficiency * motor.rotorInertia * alpha))
  return clampRatio(ideal, spec)
}

const clampRatio = (i: number, spec: DriveSpec): number =>
  Math.min(spec.ratioMax, Math.max(spec.ratioMin, i))

/** Die Übersetzung, bei der die Achse gerade noch die Höchstdrehzahl einhält. */
export function speedLimitedRatio(demand: AxisDemand, motor: Motor): number {
  const peak = demand.speed.peak
  /* Ohne bekannte Hoechstdrehzahl gibt es keine Grenze, die begrenzen koennte. */
  if (motor.speedMax <= 0) return Infinity
  if (peak < 1e-9) return Infinity
  return (motor.speedMax * 2 * Math.PI) / 60 / peak
}

/** Wie gut ein Motor mit einer Übersetzung zur Achse passt. */
export interface DriveFit {
  motor: Motor
  /** Gewählte Gesamtübersetzung. */
  ratio: number
  /** Drehzahl am Rotor, 1/min. */
  motorSpeed: number
  /** Spitzenmoment am Rotor, Nm — Last plus eigener Rotor. */
  motorMomentPeak: number
  /** Effektivmoment am Rotor, Nm. */
  motorMomentRms: number
  /** Haltemoment am Rotor, Nm. */
  motorMomentHolding: number
  /** Verlustleistung im Halten, W — `null` ohne Phasenwiderstand. */
  holdingLoss: number | null
  /** An die Achse gespiegelte Rotorträgheit, kg·m² — `null` ohne Rotordaten. */
  reflectedInertia: number | null
  /**
   * Ausnutzung: 1 heisst genau an der Grenze, `null` heisst nicht bewertbar.
   *
   * DER UNTERSCHIED ZWISCHEN 0 UND `null` IST DER GANZE PUNKT. Eine Null sagt
   * „die Grenze wird nicht beruehrt". Ein `null` sagt „ich weiss es nicht".
   * Beides als Zahl zu fuehren hiesse, fehlende Daten als bestandene Pruefung
   * zu zeigen — und genau das ist bei einem Katalog ohne Widerstandsangabe
   * passiert.
   */
  use: {
    speed: number
    peak: number
    continuous: number
    holding: number | null
  }
  /** Was dagegen spricht. Leer heisst: passt, soweit beurteilbar. */
  problems: string[]
  /**
   * Was NICHT beurteilt werden konnte, und warum.
   *
   * Steht getrennt von `problems`, weil es etwas anderes ist: ein Problem ist
   * eine Aussage ueber den Antrieb, eine Luecke eine ueber die Datenlage. Wer
   * beides in einen Topf wirft, kann einen Motor nicht mehr von einem
   * unvollstaendigen Datenblatt unterscheiden.
   */
  unknowns: string[]
}

/**
 * Rechnet einen Motor mit einer Übersetzung gegen eine Achse.
 *
 * Der Rotor trägt sich selbst mit: `J·i²` an der Achse, also `J·i·α` am Rotor.
 * Wer diesen Anteil vergisst, bekommt bei hoher Übersetzung eine Auslegung, die
 * auf dem Papier passt und im Betrieb an der eigenen Trägheit scheitert.
 */
export function evaluateDrive(
  demand: AxisDemand,
  motor: Motor,
  ratio: number,
  spec: DriveSpec = DRIVE_SPEC,
): DriveFit {
  const km = motorConstant(motor)
  const unknowns: string[] = []

  /*
    OHNE ROTORTRAEGHEIT FAELLT DER EIGENANTEIL WEG — und das macht die
    Auslegung OPTIMISTISCH, nicht neutral. Der Rotor beschleunigt sich selbst
    mit; ihn nicht zu rechnen laesst das Spitzenmoment zu klein aussehen. Die
    Luecke steht deshalb ausdruecklich am Ergebnis und nicht nur im Kommentar.
  */
  const reflected = motor.rotorInertia === null ? null : motor.rotorInertia * ratio * ratio
  const own =
    motor.rotorInertia === null ? 0 : motor.rotorInertia * ratio * demand.acceleration.peak
  if (motor.rotorInertia === null) {
    unknowns.push('Rotorträgheit unbekannt — Eigenanteil am Spitzenmoment fehlt')
  }

  const peak = demand.moment.peak / (ratio * spec.efficiency) + own
  const rms = demand.moment.rms / (ratio * spec.efficiency)
  /* Im Halten steht der Rotor still — sein eigener Trägheitsanteil entfällt. */
  const holding = demand.holdingMoment / (ratio * spec.efficiency)
  const loss = km === null ? null : Math.pow(holding / km, 2)
  if (km === null) unknowns.push('Phasenwiderstand unbekannt — Halteverlust nicht bewertbar')

  const speed = (demand.speed.peak * ratio * 60) / (2 * Math.PI)

  const bekannt = motor.speedMax > 0
  if (!bekannt) unknowns.push('Höchstdrehzahl nicht veröffentlicht — Drehzahl nicht bewertbar')

  const use = {
    speed: bekannt ? speed / motor.speedMax : 0,
    peak: peak / motor.momentPeak,
    continuous: (rms * spec.reserve) / motor.momentContinuous,
    holding: loss === null ? null : loss / spec.holdingLoss,
  }

  const problems: string[] = []
  if (bekannt && use.speed > 1) {
    problems.push(`Drehzahl ${speed.toFixed(0)} über ${motor.speedMax} 1/min`)
  }
  if (use.peak > 1) problems.push(`Spitzenmoment ${peak.toFixed(2)} über ${motor.momentPeak} Nm`)
  if (use.continuous > 1) {
    problems.push(`Effektivmoment ${rms.toFixed(2)} Nm über dem Dauermoment`)
  }
  if (use.holding !== null && use.holding > 1 && loss !== null) {
    problems.push(`Halteverlust ${loss.toFixed(0)} W über ${spec.holdingLoss} W`)
  }

  return {
    motor,
    ratio,
    motorSpeed: speed,
    motorMomentPeak: peak,
    motorMomentRms: rms,
    motorMomentHolding: holding,
    holdingLoss: loss,
    reflectedInertia: reflected,
    use,
    problems,
    unknowns,
  }
}

/**
 * Der Vorschlag für eine Achse — als VORSCHLAG, nicht als Entscheidung.
 *
 * Gereiht wird nach der grössten Ausnutzung: der Antrieb, der am weitesten von
 * allen seinen Grenzen entfernt bleibt, steht oben. Passt keiner, bleibt die
 * Liste trotzdem gefüllt, und `feasible` sagt, dass nichts passt — eine leere
 * Liste würde nur verbergen, wie knapp es war.
 */
export interface DriveProposal {
  demand: AxisDemand
  /**
   * Der ENTSCHIEDENE Antrieb, gegen die gerechnete Last — oder `null`, wenn für
   * diese Achse nichts entschieden ist.
   *
   * DIE EIGENTLICHE FRAGE DER SEITE, seit die Motorisierung feststeht. „Welchen
   * Motor bräuchte man?" ist interessant, solange nichts ausgewählt ist. Danach
   * lautet die Frage: hält die getroffene Auswahl der gerechneten Last stand?
   *
   * Sie steht VOR den Kandidaten, weil sie die wichtigere ist.
   */
  setup: DriveFit | null
  /**
   * Bester Kandidat zuerst.
   *
   * MIT EINER EINSCHRÄNKUNG, die man kennen muss: die Reihung nimmt den
   * kleinsten Motor, der passt. Das setzt voraus, dass hohe Übersetzungen sich
   * selbst bestrafen — über die Rotorträgheit, die quadratisch mitwächst. Steht
   * die auf `null`, fällt diese Bremse weg, und die Reihung landet zuverlässig
   * beim kleinsten Motor an der grössten Übersetzung. Das ist kein Vorschlag
   * mehr, sondern ein Artefakt; `rankable` sagt es.
   */
  candidates: DriveFit[]
  /**
   * Ist die Reihung überhaupt aussagekräftig?
   *
   * Falsch, sobald einem Kandidaten die Rotorträgheit fehlt. Dann bleibt die
   * Liste stehen — die Einzelprüfungen Drehzahl, Spitzen- und Effektivmoment
   * gelten weiter —, aber sie ist keine Empfehlung.
   */
  rankable: boolean
  /** Gibt es überhaupt einen passenden? */
  feasible: boolean
  /**
   * Was ein Motor können müsste, der passt — unabhängig vom Katalog.
   *
   * Die Zahl, die der Vorlage zufolge im ganzen Programm fehlt: die geforderte
   * Motorkonstante bei der besten Übersetzung, zusammen mit der Trägheit, die
   * dabei noch tragbar wäre.
   */
  required: { motorConstant: number; rotorInertia: number | null; ratio: number }
}

export function proposeDrive(
  demand: AxisDemand,
  motors: Motor[] = MOTORS,
  spec: DriveSpec = DRIVE_SPEC,
): DriveProposal {
  const candidates = motors
    .map((motor) => {
      /* Erst die Trägheitsanpassung, dann die Drehzahlgrenze — die Grenze darf
         die Anpassung nur nach unten korrigieren, nie nach oben. */
      const ratio = clampRatio(
        Math.min(matchedRatio(demand, motor, spec), speedLimitedRatio(demand, motor)),
        spec,
      )
      return evaluateDrive(demand, motor, ratio, spec)
    })
    /*
      Gereiht wird in zwei Gruppen. Was passt, steht vorn und dort nach MASSE:
      der kleinste Antrieb, der die Aufgabe erfüllt, ist der richtige — jedes
      Kilogramm im Bein zahlt die Hüfte mit Trägheit. Was nicht passt, folgt
      dahinter nach der grössten Ausnutzung, damit man sieht, wie knapp es war.
    */
    .sort((a, b) => {
      const fits = (f: DriveFit) => (f.problems.length === 0 ? 0 : 1)
      if (fits(a) !== fits(b)) return fits(a) - fits(b)
      if (fits(a) === 0) return a.motor.mass - b.motor.mass
      return worstUse(a) - worstUse(b)
    })

  return {
    demand,
    setup: setupFit(demand, spec),
    candidates,
    rankable: candidates.every((c) => c.motor.rotorInertia !== null),
    feasible: candidates.some((c) => c.problems.length === 0),
    required: requiredMotor(demand, spec),
  }
}

/* Was nicht bewertbar ist, geht nicht in die Reihung ein: eine fehlende Zahl
   darf einen Motor weder besser noch schlechter machen, als er ist. */
/**
 * Der entschiedene Antrieb dieser Achse gegen die gerechnete Last.
 *
 * ZWEI MOTOREN AUF EINER ACHSE TEILEN SICH DAS MOMENT, und deshalb wird hier
 * nicht der Katalogmotor eingesetzt, sondern einer mit der Summe der Momente.
 * Hüfte sagittal und Knie tragen je zwei ILM115; wer sie einzeln prüft, bekommt
 * die doppelte Ausnutzung und ein Urteil, das nicht stimmt.
 *
 * Die Drehzahl bleibt die des einzelnen Motors — parallel geschaltete Motoren
 * drehen gleich schnell, sie addieren Moment und nicht Drehzahl.
 */
export function setupFit(demand: AxisDemand, spec: DriveSpec = DRIVE_SPEC): DriveFit | null {
  const axis = setAxisFor(demand.joint, demand.plane)
  if (!axis) return null
  const motor = MOTORS.find((m) => m.id === axis.motorId)
  if (!motor) return null

  const zusammen: Motor =
    axis.motorsPerAxis > 1
      ? {
          ...motor,
          label: `${axis.motorsPerAxis}× ${motor.label}`,
          momentPeak: motor.momentPeak * axis.motorsPerAxis,
          momentContinuous: motor.momentContinuous * axis.motorsPerAxis,
          mass: motor.mass * axis.motorsPerAxis,
        }
      : motor

  return evaluateDrive(demand, zusammen, axis.ratio, spec)
}

const worstUse = (fit: DriveFit): number =>
  Math.max(fit.use.speed, fit.use.peak, fit.use.continuous, fit.use.holding ?? 0)

/**
 * Was ein Motor können müsste — ohne Katalog.
 *
 * Die Übersetzung folgt aus der Drehzahlgrenze, die sich hier umgekehrt aus
 * einer angenommenen Rotordrehzahl ergibt; daraus folgen die geforderte
 * Motorkonstante (aus dem Halteverlust) und die noch tragbare Rotorträgheit
 * (aus dem Spitzenmoment). Das ist die Zahl, mit der man einen Hersteller
 * anspricht — nicht eine Wattangabe.
 */
function requiredMotor(
  demand: AxisDemand,
  spec: DriveSpec,
): { motorConstant: number; rotorInertia: number | null; ratio: number } {
  const ratio = clampRatio(REQUIRED_SPEED_RATIO(demand), spec)
  const holding = demand.holdingMoment / (ratio * spec.efficiency)
  const km = holding / Math.sqrt(spec.holdingLoss)

  /* Die Trägheit darf höchstens so gross sein, dass ihr eigener Anteil die
     Hälfte des verfügbaren Spitzenmoments nicht überschreitet. */
  const peak = demand.moment.peak / (ratio * spec.efficiency)
  const alpha = demand.acceleration.peak
  /* Ohne Beschleunigung gibt es keine Trägheitsforderung — dann steht hier
     `null` und nicht eine unendlich grosse Zahl, die wie ein Wert aussieht. */
  const inertia = alpha > 1e-6 ? (0.5 * peak) / (ratio * alpha) : null

  return { motorConstant: km, rotorInertia: inertia, ratio }
}

/** Angenommene Rotordrehzahl der Auslegung, 1/min. */
export const NOMINAL_MOTOR_SPEED = 5000

const REQUIRED_SPEED_RATIO = (demand: AxisDemand): number => {
  const peak = demand.speed.peak
  if (peak < 1e-9) return DRIVE_SPEC.ratioMax
  return (NOMINAL_MOTOR_SPEED * 2 * Math.PI) / 60 / peak
}

/**
 * Die ganze Liste — je Achse eine Zeile.
 *
 * Das ist die „dynamisch mitgepflegte Motorenliste": sie wird nirgends
 * gespeichert, sondern bei jeder Änderung an Gelenkpunkten, Massen oder
 * Gangaufgabe neu gerechnet. Eine gespeicherte Liste wäre eine zweite Wahrheit
 * und liefe beim ersten verschobenen Punkt aus dem Ruder.
 */
export function driveList(
  model: HumanoidModel,
  gait: GaitResult,
  dynamics: DynamicsResult,
  off: OffSet = {},
  motors: Motor[] = MOTORS,
  spec: DriveSpec = DRIVE_SPEC,
): DriveProposal[] {
  return axisDemands(model, gait, dynamics, off).map((demand) => proposeDrive(demand, motors, spec))
}
